import { useCallback } from 'react';
import { MessageProps, useMessages } from '@chatui/core';
import type { IMessageItem } from '../../types';

interface IMessageOptions {
  /** 用户头像url */
  userAvatar?: string;
  /** 机器人头像url */
  robotAvatar?: string;
  /** 是否展示用户头像 */
  showUser?: boolean;
  /** 是否展示机器人头像 */
  showRobot?: boolean;
  /** 初始化时的消息内容 */
  defaultMessages?: IMessageItem[];
}

export default function useChatAIMessages(options: IMessageOptions = {}) {
  const { defaultMessages = [] } = options;

  // 消息格式化，主要作用是融合配置项中的头像配置
  const formatMessage = useCallback(
    (message: IMessageItem) => {
      const data: MessageProps = {
        ...message,
        type: message.type || 'text',
      };
      const showAvatar = message.content?.showAvatar || true;
      const avatarUrl = message.content?.avatarUrl;
      if (message.position === 'right' && options.showUser && showAvatar) {
        data.user = { avatar: avatarUrl || options.userAvatar };
      }
      if (message.position === 'left' && options.showRobot && showAvatar) {
        data.user = { avatar: avatarUrl || options.robotAvatar };
      }
      return data;
    },
    [options],
  );

  const {
    messages,
    appendMsg: uiAppendMsg,
    prependMsgs: uiPrependMsgs,
    resetList: uiResetList,
    updateMsg: uiUpdateMsg,
  } = useMessages(defaultMessages.map((msg) => formatMessage(msg)));

  const appendMsg = useCallback(
    (message: IMessageItem) => {
      uiAppendMsg(formatMessage(message));
    },
    [uiAppendMsg, formatMessage],
  );

  const updateMsg = useCallback(
    (id: number | string, message: IMessageItem) => {
      uiUpdateMsg(id, formatMessage(message));
    },
    [uiUpdateMsg, formatMessage],
  );

  const prependMsgs = useCallback(
    (_messages: IMessageItem[]) => {
      uiPrependMsgs(_messages.map((msg) => formatMessage(msg)));
    },
    [uiPrependMsgs, formatMessage],
  );

  const resetList = useCallback(
    (_messages: IMessageItem[]) => {
      uiResetList(_messages.map((msg) => formatMessage(msg)));
    },
    [uiResetList, formatMessage],
  );

  return {
    messages: messages as IMessageItem[],
    appendMsg,
    updateMsg,
    prependMsgs,
    resetList,
  };
}
